import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/classNameHelpers';

const SCROLL_THRESHOLD = 1;

type FadeSizes = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

type ScrollContainerProps = {
  children: React.ReactNode;
  className?: string;
  direction?: 'horizontal' | 'vertical' | 'all';
  fadeSize?: number | FadeSizes;
  style?: React.CSSProperties;
};

export function ScrollContainer({
  children,
  className,
  direction = 'horizontal',
  fadeSize = 40,
  style,
}: ScrollContainerProps) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleHorizontal = direction === 'horizontal' || direction === 'all';
  const handleVertical = direction === 'vertical' || direction === 'all';

  // Normalize fade sizes
  const fadeSizes: FadeSizes =
    typeof fadeSize === 'number'
      ? { top: fadeSize, bottom: fadeSize, left: fadeSize, right: fadeSize }
      : fadeSize;

  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth, scrollTop, scrollHeight, clientHeight } =
          scrollRef.current;

        // Check horizontal scroll
        if (handleHorizontal) {
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD);
        }

        // Check vertical scroll
        if (handleVertical) {
          setCanScrollTop(scrollTop > 0);
          setCanScrollBottom(scrollTop < scrollHeight - clientHeight - SCROLL_THRESHOLD);
        }
      }
    };

    const scrollContainer = scrollRef.current;

    if (scrollContainer) {
      checkScroll();
      scrollContainer.addEventListener('scroll', checkScroll);

      // Also check on resize
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(scrollContainer);

      return () => {
        scrollContainer.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }

    return undefined;
  }, [handleHorizontal, handleVertical]);

  const getMaskImage = () => {
    const masks: string[] = [];

    // Build horizontal mask
    if (handleHorizontal) {
      const leftColor = canScrollLeft ? 'transparent' : 'black';
      const rightColor = canScrollRight ? 'transparent' : 'black';
      const leftSize = fadeSizes.left || 0;
      const rightSize = fadeSizes.right || 0;

      masks.push(
        `linear-gradient(to right, ${leftColor} 0%, black ${leftSize}px, black calc(100% - ${rightSize}px), ${rightColor} 100%)`
      );
    }

    // Build vertical mask
    if (handleVertical) {
      const topColor = canScrollTop ? 'transparent' : 'black';
      const bottomColor = canScrollBottom ? 'transparent' : 'black';
      const topSize = fadeSizes.top || 0;
      const bottomSize = fadeSizes.bottom || 0;

      masks.push(
        `linear-gradient(to bottom, ${topColor} 0%, black ${topSize}px, black calc(100% - ${bottomSize}px), ${bottomColor} 100%)`
      );
    }

    // If both directions, combine masks
    return masks.length > 1 ? masks.join(', ') : masks[0] || 'none';
  };

  return (
    <div
      ref={scrollRef}
      className={cn(
        'scrollbar-hide scroll-smooth',
        {
          'overflow-x-auto': handleHorizontal,
          'overflow-y-auto': handleVertical,
        },
        className
      )}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        maskImage: getMaskImage(),
        WebkitMaskImage: getMaskImage(),
        ...style,
      }}
    >
      {children}
    </div>
  );
}
